import { useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    getMonth,
    getYear,
    isSameDay,
    isSameMonth,
    setMonth,
    setYear,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import { es } from 'date-fns/locale';

const WEEK_DAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
    value: index,
    label: format(new Date(2024, index, 1), 'LLLL', { locale: es }),
}));

const buildYearOptions = (anchorDate) => {
    const anchorYear = getYear(anchorDate);
    const todayYear = getYear(new Date());
    const min = Math.min(anchorYear, todayYear) - 3;
    const max = Math.max(anchorYear, todayYear) + 6;
    return Array.from({ length: max - min + 1 }, (_, index) => min + index);
};

/**
 * Navegador de mes compacto. Marca con un punto los días que tienen eventos
 * para que el mes sirva como mapa de carga, no solo como selector.
 */
export const MiniMonth = ({ value, onChange, markedDayKeys, eventDates = [] }) => {
    const monthStart = startOfMonth(value);
    const monthIndex = getMonth(value);
    const yearValue = getYear(value);
    const yearOptions = useMemo(() => buildYearOptions(value), [value]);

    const days = useMemo(() => eachDayOfInterval({
        start: startOfWeek(monthStart, { weekStartsOn: 0 }),
        end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 0 }),
    }), [monthStart]);

    const markedDays = useMemo(() => {
        if (markedDayKeys) return markedDayKeys;
        return new Set(eventDates.map((date) => format(date, 'yyyy-MM-dd')));
    }, [markedDayKeys, eventDates]);

    const today = new Date();

    const handleMonthChange = (event) => {
        onChange(setMonth(value, Number(event.target.value)));
    };

    const handleYearChange = (event) => {
        onChange(setYear(value, Number(event.target.value)));
    };

    return (
        <div className="mini-month">
            <div className="mini-month__header">
                <div className="mini-month__selects">
                    <label className="mini-month__select-wrap">
                        <span className="sr-only">Mes</span>
                        <select
                            value={monthIndex}
                            onChange={handleMonthChange}
                            className="mini-month__select mini-month__select--month"
                            aria-label="Seleccionar mes"
                        >
                            {MONTH_OPTIONS.map((month) => (
                                <option key={month.value} value={month.value}>
                                    {month.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="mini-month__select-wrap mini-month__select-wrap--year">
                        <select
                            value={yearValue}
                            onChange={handleYearChange}
                            className="mini-month__select mini-month__select--year"
                            aria-label="Seleccionar año"
                        >
                            {yearOptions.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="mini-month__nav">
                    <button
                        type="button"
                        onClick={() => onChange(addMonths(value, -1))}
                        className="mini-month__nav-btn"
                        aria-label="Mes anterior"
                    >
                        <ChevronLeftIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onChange(addMonths(value, 1))}
                        className="mini-month__nav-btn"
                        aria-label="Mes siguiente"
                    >
                        <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <div className="mini-month__grid">
                {WEEK_DAYS.map((day, index) => (
                    <span
                        key={`${day}-${index}`}
                        className="mini-month__weekday"
                        aria-hidden="true"
                    >
                        {day}
                    </span>
                ))}

                {days.map((day) => {
                    const isSelected = isSameDay(day, value);
                    const isToday = isSameDay(day, today);
                    const inMonth = isSameMonth(day, monthStart);
                    const hasEvents = markedDays.has(format(day, 'yyyy-MM-dd'));

                    return (
                        <button
                            key={day.toISOString()}
                            type="button"
                            onClick={() => onChange(day)}
                            aria-current={isToday ? 'date' : undefined}
                            aria-label={format(day, "d 'de' MMMM yyyy", { locale: es })}
                            className={`mini-month__day${
                                isSelected ? ' mini-month__day--selected' : ''
                            }${isToday && !isSelected ? ' mini-month__day--today' : ''}${
                                !inMonth ? ' mini-month__day--outside' : ''
                            }`}
                        >
                            {format(day, 'd')}
                            {hasEvents && (
                                <span
                                    className={`mini-month__dot${
                                        isSelected ? ' mini-month__dot--selected' : ''
                                    }`}
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
